import type { FunkitCheckoutConfig } from '@funkit/connect';
import { createAaveSupplyCheckoutConfig } from '@funkit/connect/clients/aave';
import { CustomMarket } from 'src/ui-config/marketsConfig';
import { type Address, getAddress } from 'viem';

/**
 * fun checkout only ships on the Core mainnet market. Gating on the market key
 * (not chainId) matters: mainnet hosts three markets (Core, Prime/Lido, EtherFi)
 * and e.g. USDC exists in all three with a different aToken and pool in each.
 * With the market pinned here, every address the reserve hands us (underlying,
 * aToken, pool) is consistent by construction.
 */
const FUN_SUPPLY_MARKET = CustomMarket.proto_mainnet_v3;

/**
 * The product allowlist — the underlyings (lowercased) whose Supply button
 * routes through funkit's checkout modal instead of the native Aave supply
 * modal. Everything else about these assets (symbols, decimals, addresses,
 * icons) comes from live app state at click time.
 */
export const FUN_SUPPLY_UNDERLYINGS: ReadonlySet<string> = new Set([
  '0xcbb7c0000ab88b473b1f5afd9ef808440eed33bf', // cbBTC
  '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599', // WBTC
  '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
  '0xdac17f958d2ee523a2206206994597c13d831ec7', // USDT
]);

/** True when this market+asset's Supply button should open the funkit modal. */
export function isFunSupplyAsset(market: CustomMarket, underlyingAsset: string): boolean {
  return market === FUN_SUPPLY_MARKET && FUN_SUPPLY_UNDERLYINGS.has(underlyingAsset.toLowerCase());
}

/**
 * Reserve snapshot the Supply button hands off when opening the fun modal.
 * Display fields come from the clicked dashboard reserve; receipt-token fields
 * come from the SDK reserve in `useAppDataContext().supplyReserves` (its
 * `aToken`/`underlyingToken` are `@aave/graphql` `Currency` objects — the same
 * source AddTokenDropdown renders on reserve-overview).
 */
export type FunSupplyReserve = {
  underlyingAsset: string;
  /** Patched display symbol of the underlying (the list item's `symbol`). */
  symbol: string;
  /**
   * Ringed aToken icon (Base64Token data URI), generated by the supply row via
   * useFunSupplyATokenIcon — the same image the native flow registers via
   * wallet_watchAsset. Undefined when generation hasn't completed (or was
   * skipped); the hosted `aToken.imageUrl` is the fallback.
   */
  aTokenBase64?: string;
  /** Aave's `supplyAPY` — a 0–1 fraction string (e.g. "0.0283"). */
  supplyAPY: string | number;
  /** The user's collateral toggle for this reserve (`usageAsCollateralEnabledOnUser`). */
  collateralEnabled: boolean;
  chainId: number;
  /** The market's pool (`currentMarketData.addresses.LENDING_POOL`). */
  poolAddress: string;
  /** `sdkReserve.underlyingToken.imageUrl` — absolute URL. */
  underlyingImageUrl: string;
  /** `sdkReserve.aToken` — the receipt token's own on-chain metadata. */
  aToken: { address: string; symbol: string; decimals: number; imageUrl: string };
};

// Aave's supplyAPY is a 0–1 fraction; funkit's `display.supplyAPY` wants a
// percent string without the % sign (e.g. "2.83"). Rates that would render as
// 0.00 floor to "<0.01" (Aave's FormattedNumber convention) — the modal styles
// the APY as earnings-green, which a literal 0.00% would contradict. funkit
// only string-interpolates this value, so the non-numeric form is safe.
function toPercentString(apy: string | number): string {
  const fraction = Number(apy);
  if (!Number.isFinite(fraction)) {
    return '0';
  }
  const percent = (fraction * 100).toFixed(2);
  return percent === '0.00' ? '<0.01' : percent;
}

/**
 * Builds the per-asset funkit checkout config. The allowlist+market gate lives
 * at the click site (`useSupplyButtonAction`); this trusts the vetted reserve.
 * Returns `undefined` only when the chain isn't fun-supported
 * (createAaveSupplyCheckoutConfig's own signal).
 */
export function buildFunSupplyConfig(
  reserve: FunSupplyReserve,
  walletAddress: Address | undefined,
  /**
   * Health-factor resolver the funkit confirmation screen calls with the live
   * supply amount. We own the math (live app-data + `calculateHFAfterSupply`)
   * so the number matches the dashboard; the SDK only renders it.
   */
  resolveHealthFactor?: (
    underlyingHumanAmount: string
  ) => { before: string | null; after: string | null } | null
): FunkitCheckoutConfig | undefined {
  return createAaveSupplyCheckoutConfig({
    underlyingAsset: getAddress(reserve.underlyingAsset),
    poolAddress: getAddress(reserve.poolAddress),
    chainId: reserve.chainId,
    walletAddress,
    display: {
      symbol: reserve.symbol,
      supplyAPY: toPercentString(reserve.supplyAPY),
      collateralizationEnabled: reserve.collateralEnabled,
      iconSrc: reserve.underlyingImageUrl,
    },
    receiptToken: {
      address: getAddress(reserve.aToken.address),
      symbol: reserve.aToken.symbol,
      decimals: reserve.aToken.decimals,
      iconSrc: reserve.aTokenBase64 ?? reserve.aToken.imageUrl,
    },
    resolveHealthFactor,
  });
}
